/**
 * Advanced Config Hook
 * Provides computed styles from advanced widget configuration
 * Implements High Priority items: Typography, Message Bubbles, Spacing, Avatar, Send Button
 */

import React, { useMemo } from 'react';

import { useEmbedConfig } from '../providers/EmbedConfigProvider';
import { useEmbedTheme } from '../theme';
import { contrastRatio } from '../theme/contrast';
import type { AdvancedWidgetConfig } from '../theme/types';
import { DEFAULT_ADVANCED_CONFIG } from '../theme/types';

// ============================================================================
// Types
// ============================================================================

export interface AdvancedStyles {
  /** Typography styles to apply to the widget container */
  typography: React.CSSProperties;

  /** Message bubble styles for user messages */
  userBubble: React.CSSProperties;

  /** Message bubble styles for agent messages */
  agentBubble: React.CSSProperties;

  /** Container spacing styles */
  containerSpacing: React.CSSProperties;

  /** Message gap value in pixels */
  messageGap: number;

  /** Avatar styles for agent */
  agentAvatar: React.CSSProperties & { show: boolean };

  /** Avatar styles for user */
  userAvatar: React.CSSProperties & { show: boolean };

  /** Send button styles */
  sendButton: React.CSSProperties;

  /** Send button icon style */
  sendButtonIcon: 'arrow' | 'send' | 'paper-plane';

  /** Whether to show send button only with text */
  sendButtonShowOnlyWithText: boolean;
}

// ============================================================================
// Font Family Mapping
// ============================================================================

const FONT_FAMILY_MAP: Record<string, string> = {
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  inter: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  roboto: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  'open-sans': '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  lato: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  poppins: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

// ============================================================================
// Shadow Mapping
// ============================================================================

const SHADOW_MAP = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
};

// ============================================================================
// Avatar Shape Mapping
// ============================================================================

const AVATAR_SHAPE_MAP = {
  circle: '9999px',
  rounded: '8px',
  square: '4px',
};

// ============================================================================
// Send Button Variant Styles
// ============================================================================

function getSendButtonVariantStyles(
  variant: 'filled' | 'outline' | 'ghost' | 'icon-only',
  primaryColor: string,
): React.CSSProperties {
  switch (variant) {
    case 'filled':
      return {
        backgroundColor: primaryColor,
        color: '#ffffff',
        border: 'none',
      };
    case 'outline':
      return {
        backgroundColor: 'transparent',
        color: primaryColor,
        border: `2px solid ${primaryColor}`,
      };
    case 'ghost':
      return {
        backgroundColor: 'transparent',
        color: primaryColor,
        border: 'none',
      };
    case 'icon-only':
      return {
        backgroundColor: 'transparent',
        color: primaryColor,
        border: 'none',
        padding: '4px',
      };
    default:
      return {
        backgroundColor: primaryColor,
        color: '#ffffff',
        border: 'none',
      };
  }
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useAdvancedConfig(): {
  config: AdvancedWidgetConfig;
  styles: AdvancedStyles;
  hasAdvancedConfig: boolean;
} {
  const { advancedConfig } = useEmbedConfig();
  const { primaryColor, theme } = useEmbedTheme();

  // Merge with defaults
  const config = useMemo((): AdvancedWidgetConfig => {
    if (!advancedConfig) return DEFAULT_ADVANCED_CONFIG;

    // Deep merge with defaults
    return {
      ...DEFAULT_ADVANCED_CONFIG,
      ...advancedConfig,
      messageBubbles: {
        ...DEFAULT_ADVANCED_CONFIG.messageBubbles,
        ...advancedConfig.messageBubbles,
        user: {
          ...DEFAULT_ADVANCED_CONFIG.messageBubbles.user,
          ...advancedConfig.messageBubbles?.user,
        },
        agent: {
          ...DEFAULT_ADVANCED_CONFIG.messageBubbles.agent,
          ...advancedConfig.messageBubbles?.agent,
        },
      },
      typography: {
        ...DEFAULT_ADVANCED_CONFIG.typography,
        ...advancedConfig.typography,
      },
      spacing: {
        ...DEFAULT_ADVANCED_CONFIG.spacing,
        ...advancedConfig.spacing,
      },
      avatar: {
        ...DEFAULT_ADVANCED_CONFIG.avatar,
        ...advancedConfig.avatar,
      },
      sendButton: {
        ...DEFAULT_ADVANCED_CONFIG.sendButton,
        ...advancedConfig.sendButton,
      },
    };
  }, [advancedConfig]);

  // Compute styles
  const styles = useMemo((): AdvancedStyles => {
    const { typography, messageBubbles, spacing, avatar, sendButton } = config;

    // Typography styles
    const fontFamily =
      typography.fontFamily === 'custom'
        ? typography.customFontFamily || FONT_FAMILY_MAP.system
        : typography.fontFamily === 'system'
        ? // The default follows the widget theme's font (the brand font, or the host page's
          // font when the widget inherits it) instead of forcing the system stack.
          'inherit'
        : FONT_FAMILY_MAP[typography.fontFamily] || 'inherit';

    const typographyStyles: React.CSSProperties = {
      fontFamily,
      fontSize: `${typography.baseFontSize}px`,
      lineHeight: typography.lineHeight,
      letterSpacing: `${typography.letterSpacing}em`,
      fontWeight: typography.fontWeight,
    };

    // User message bubble styles
    const userBubbleStyles: React.CSSProperties = {
      backgroundColor: messageBubbles.user.background || primaryColor,
      color: messageBubbles.user.textColor,
      borderRadius: `${messageBubbles.user.borderRadius}rem`,
      boxShadow: SHADOW_MAP[messageBubbles.user.shadow],
      padding:
        messageBubbles.padding === 'compact'
          ? '6px 10px'
          : messageBubbles.padding === 'spacious'
          ? '14px 18px'
          : '10px 14px',
      maxWidth: `${messageBubbles.maxWidth}%`,
      fontSize: `${typography.messageFontSize}px`,
      wordBreak: 'break-word',
    };

    // Agent message bubble styles
    const agentBackground =
      messageBubbles.agent.background ||
      (theme.mode === 'dark' ? theme.colors.background.elevated : theme.colors.background.input);
    const agentBubbleStyles: React.CSSProperties = {
      backgroundColor: agentBackground,
      // A custom bubble colour without a text colour gets whichever of light / dark text reads on it.
      color:
        messageBubbles.agent.textColor ||
        (messageBubbles.agent.background
          ? contrastRatio(agentBackground, '#ffffff') >= 4.5
            ? '#ffffff'
            : '#1f2937'
          : theme.colors.text.primary),
      borderRadius: `${messageBubbles.agent.borderRadius}rem`,
      boxShadow: SHADOW_MAP[messageBubbles.agent.shadow],
      padding:
        messageBubbles.padding === 'compact'
          ? '6px 10px'
          : messageBubbles.padding === 'spacious'
          ? '14px 18px'
          : '10px 14px',
      maxWidth: `${messageBubbles.maxWidth}%`,
      fontSize: `${typography.messageFontSize}px`,
      wordBreak: 'break-word',
    };

    // Container spacing styles
    const containerSpacingStyles: React.CSSProperties = {
      padding: `${spacing.widgetPadding}px`,
    };

    // Agent avatar styles
    const agentAvatarStyles: React.CSSProperties & { show: boolean } = {
      show: avatar.showAgentAvatar,
      width: `${avatar.size}px`,
      height: `${avatar.size}px`,
      borderRadius: AVATAR_SHAPE_MAP[avatar.shape],
      borderWidth: `${avatar.borderWidth}px`,
      borderStyle: avatar.borderWidth > 0 ? 'solid' : 'none',
      borderColor: avatar.borderColor,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      alignSelf:
        avatar.position === 'top' ? 'flex-start' : avatar.position === 'bottom' ? 'flex-end' : 'center',
    };

    // User avatar styles
    const userAvatarStyles: React.CSSProperties & { show: boolean } = {
      show: avatar.showUserAvatar,
      width: `${avatar.size}px`,
      height: `${avatar.size}px`,
      borderRadius: AVATAR_SHAPE_MAP[avatar.shape],
      borderWidth: `${avatar.borderWidth}px`,
      borderStyle: avatar.borderWidth > 0 ? 'solid' : 'none',
      borderColor: avatar.borderColor,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      alignSelf:
        avatar.position === 'top' ? 'flex-start' : avatar.position === 'bottom' ? 'flex-end' : 'center',
    };

    // Send button styles
    const sendButtonShape =
      sendButton.shape === 'circle' ? '9999px' : sendButton.shape === 'square' ? '4px' : '8px';

    const sendButtonStyles: React.CSSProperties = {
      width: `${sendButton.size}px`,
      height: `${sendButton.size}px`,
      borderRadius: sendButtonShape,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s ease',
      ...getSendButtonVariantStyles(sendButton.variant, primaryColor),
    };

    return {
      typography: typographyStyles,
      userBubble: userBubbleStyles,
      agentBubble: agentBubbleStyles,
      containerSpacing: containerSpacingStyles,
      messageGap: spacing.messageGap,
      agentAvatar: agentAvatarStyles,
      userAvatar: userAvatarStyles,
      sendButton: sendButtonStyles,
      sendButtonIcon: sendButton.icon,
      sendButtonShowOnlyWithText: sendButton.showOnlyWithText,
    };
  }, [config, primaryColor, theme]);

  return {
    config,
    styles,
    hasAdvancedConfig: !!advancedConfig,
  };
}

export default useAdvancedConfig;
